import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { Get, Post, Put } from '../../../../../utils/httpProvider';
import { 
    NHOMTIEUCHI_API, 
    TIEUCHI_API, 
    MUCDANHGIA_API, 
    CHITIETKETQUANCS_API,
    DECUONGNGHIENCUU_API,
    BaseGatewayImage
} from '../../../../../utils/apiUrl';

const ChamDiemDeCuongNghienCuu = ({ dataDeCuong, readOnly = false }) => {
    const deCuongNghienCuuId = dataDeCuong?.id;
    const [ketQuas, setKetQuas] = useState({});
    const [nhanXet, setNhanXet] = useState(dataDeCuong?.nhanXet || '');
    const [saving, setSaving] = useState(false);

    // Fetch data
    const { data: nhomTieuChiRes, isLoading: load1 } = useQuery('nhomTieuChiAll', () => Get(NHOMTIEUCHI_API.GET_PAGING + '?pageSize=1000'));
    const { data: tieuChiRes, isLoading: load2 } = useQuery('tieuChiAll', () => Get(TIEUCHI_API.GET_PAGING + '?pageSize=1000'));
    const { data: mucDanhGiaRes, isLoading: load3 } = useQuery('mucDanhGiaAll', () => Get(MUCDANHGIA_API.GET_PAGING + '?pageSize=1000'));
    
    // Fetch existing results for this De Cuong
    const { data: ketQuaRes, isLoading: load4, refetch: refetchKetQua } = useQuery(
        ['ketQuaNCS', deCuongNghienCuuId], 
        () => Get(CHITIETKETQUANCS_API.GET_PAGING, null, { pageIndex: 1, pageSize: 1000, DeCuongNghienCuuId: deCuongNghienCuuId }),
        { enabled: !!deCuongNghienCuuId }
    );

    const isLoading = load1 || load2 || load3 || load4;

    useEffect(() => {
        if (ketQuaRes && !load4) {
            let listKetQua = [];
            if (Array.isArray(ketQuaRes)) listKetQua = ketQuaRes;
            else if (Array.isArray(ketQuaRes?.data)) listKetQua = ketQuaRes.data;
            else if (Array.isArray(ketQuaRes?.data?.data)) listKetQua = ketQuaRes.data.data;
            else if (Array.isArray(ketQuaRes?.data?.$values)) listKetQua = ketQuaRes.data.$values;
            else if (Array.isArray(ketQuaRes?.Data)) listKetQua = ketQuaRes.Data;
            
            const newKetQuas = {};
            listKetQua.forEach(kq => {
                const tcId = (kq.tieuChiId || kq.TieuChiId)?.toLowerCase();
                if (tcId) {
                    newKetQuas[tcId] = {
                        id: kq.id || kq.Id,
                        tieuChiId: tcId,
                        mucDanhGiaId: (kq.mucDanhGiaId || kq.MucDanhGiaId)?.toLowerCase(),
                        tenMucDanhGia: kq.tenMucDanhGia || kq.TenMucDanhGia,
                        diemSo: kq.diemSo !== undefined ? kq.diemSo : kq.DiemSo,
                        deCuongNghienCuuId: kq.deCuongNghienCuuId || kq.DeCuongNghienCuuId,
                        nhanXet: kq.nhanXet || kq.NhanXet
                    };
                }
            });
            setKetQuas(newKetQuas);
        }
    }, [ketQuaRes]);

    if (!deCuongNghienCuuId) {
        return <Alert variant="warning">Chưa có thông tin đề cương nghiên cứu để chấm điểm.</Alert>;
    }

    if (isLoading) {
        return (
            <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Đang tải bảng tiêu chí...</p>
            </div>
        );
    }

    // Parse Data
    const nhomTieuChis = (nhomTieuChiRes?.data?.data || nhomTieuChiRes?.data?.$values || nhomTieuChiRes?.data || []).sort((a, b) => (a.phan || 0) - (b.phan || 0));
    const tieuChis = tieuChiRes?.data?.data || tieuChiRes?.data?.$values || tieuChiRes?.data || [];
    const mucDanhGias = mucDanhGiaRes?.data?.data || mucDanhGiaRes?.data?.$values || mucDanhGiaRes?.data || [];

    // Grouping
    const tableData = nhomTieuChis.map(nhom => {
        const tcs = tieuChis.filter(tc => tc.nhomTieuChiId === nhom.id).sort((a, b) => (a.thuTu || 0) - (b.thuTu || 0));
        return {
            ...nhom,
            tieuChis: tcs.map(tc => {
                const mdgs = mucDanhGias.filter(mdg => mdg.tieuChiId === tc.id).sort((a, b) => (b.diemSo || 0) - (a.diemSo || 0));
                return {
                    ...tc,
                    mucDanhGias: mdgs
                };
            })
        };
    });

    const handleSelectMucDanhGia = (tieuChiId, mucDanhGia) => {
        if (readOnly) return;
        const tId = tieuChiId?.toLowerCase();
        setKetQuas(prev => ({
            ...prev,
            [tId]: {
                ...(prev[tId] || {}),
                tieuChiId: tieuChiId,
                mucDanhGiaId: mucDanhGia.id?.toLowerCase(),
                tenMucDanhGia: mucDanhGia.tenMucDanhGia,
                diemSo: mucDanhGia.diemSo,
                deCuongNghienCuuId: deCuongNghienCuuId
            }
        }));
    };

    const handleDiemSoChange = (tieuChiId, value) => {
        if (readOnly) return;
        const tId = tieuChiId?.toLowerCase();
        setKetQuas(prev => ({
            ...prev,
            [tId]: {
                ...(prev[tId] || {}),
                tieuChiId: tieuChiId,
                diemSo: value ? parseFloat(value) : null,
                deCuongNghienCuuId: deCuongNghienCuuId
            }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const listToSave = Object.values(ketQuas).filter(x => x.diemSo !== null && x.diemSo !== undefined && x.diemSo !== '');
            if (listToSave.length === 0) {
                toast.warning('Vui lòng chấm điểm ít nhất 1 tiêu chí.');
                setSaving(false);
                return;
            }

            const promises = listToSave.map(kq => {
                if (kq.id) {
                    return Put(CHITIETKETQUANCS_API.UPDATE_ITEM, kq.id, kq);
                } else {
                    return Post(CHITIETKETQUANCS_API.CREATE_ITEM, null, kq);
                }
            });

            const dataDeCuongUpdate = {
                ...dataDeCuong,
                nhanXet: nhanXet
            };
            promises.push(Put(DECUONGNGHIENCUU_API.UPDATE_ITEM, deCuongNghienCuuId, dataDeCuongUpdate));

            await Promise.all(promises);
            toast.success('Lưu điểm thành công!');
            refetchKetQua();
        } catch (error) {
            console.error(error);
            toast.error('Có lỗi xảy ra khi lưu điểm.');
        } finally {
            setSaving(false);
        }
    };

    // Calculate total score
    let totalScore = 0;
    Object.values(ketQuas).forEach(kq => {
        if (kq.diemSo) totalScore += parseFloat(kq.diemSo);
    });

    // Helper function to get correct URL for files
    const getSafeUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
        const safePath = path.startsWith('/') ? path : `/${path}`;
        return `${baseUrl}${safePath}`;
    };

    return (
        <div className="bg-light p-3 rounded border">
            <div 
                className="mb-4 p-3 rounded shadow-sm" 
                style={{ backgroundColor: '#f4f7fb', border: '1px solid #e1e8f0', borderLeft: '4px solid #4a90e2' }}
            >
                <h6 className="font-weight-bold mb-3" style={{ color: '#2c3e50' }}>
                    <i className="fas fa-info-circle mr-2" style={{ color: '#4a90e2' }}></i>
                    Thông tin đề cương nghiên cứu
                </h6>
                <div className="row" style={{ color: '#495057' }}>
                    <div className="col-md-12 mb-2">
                        <strong style={{ color: '#34495e' }}>Tên đề cương: </strong> {dataDeCuong?.tenDeCuong || <span className="text-muted font-italic">Chưa cập nhật</span>}
                    </div>
                    <div className="col-md-12 mb-2">
                        <strong style={{ color: '#34495e' }}>File minh chứng: </strong> 
                        {dataDeCuong?.fileMinhChung ? (
                            <div className="mt-1">
                                <a href={getSafeUrl(dataDeCuong.fileMinhChung)} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm text-left" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={dataDeCuong.fileMinhChung.split('/').pop()}>
                                    <i className="fas fa-paperclip mr-1"></i> {dataDeCuong.fileMinhChung.split('/').pop()}
                                </a>
                            </div>
                        ) : <span className="text-muted font-italic">Không có</span>}
                    </div>
                    {dataDeCuong?.canBoHuongDans && dataDeCuong.canBoHuongDans.length > 0 && (
                        <div className="col-md-12">
                            <strong style={{ color: '#34495e' }}>Cán bộ hướng dẫn: </strong>
                            <ul className="mb-0 mt-1 pl-4">
                                {dataDeCuong.canBoHuongDans.map((cb, idx) => (
                                    <li key={idx}>
                                        {cb.hoTen} {cb.donViCongTac ? `- ${cb.donViCongTac}` : ''}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            <h5 className="font-weight-bold text-primary mb-3">Bảng chấm điểm đề cương nghiên cứu</h5>
            
            <div className="table-responsive">
                <Table bordered size="sm" className="bg-white">
                    <thead className="bg-light text-center">
                        <tr>
                            <th className="text-center align-middle" style={{ width: '50px' }}>TT</th>
                            <th className="text-center align-middle">Nội dung chấm</th>
                            <th className="text-center align-middle" style={{ width: '200px' }}>Mức đánh giá</th>
                            <th className="text-center align-middle" style={{ width: '100px' }}>Thang điểm</th>
                            <th className="text-center align-middle" style={{ width: '120px' }}>Điểm đạt</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map((nhom, indexNhom) => (
                            <React.Fragment key={nhom.id}>
                                <tr className="bg-light font-weight-bold">
                                    <td colSpan={5}>
                                        Phần {nhom.phan || indexNhom + 1}: {nhom.tenNhomTieuChi} ({nhom.diemToiDa}đ)
                                    </td>
                                </tr>
                                {nhom.tieuChis.map((tc, indexTc) => {
                                    const rowSpan = tc.mucDanhGias.length || 1;
                                    const ketQuaHienTai = ketQuas[tc.id?.toLowerCase()] || {};
                                    
                                    return (
                                        <React.Fragment key={tc.id}>
                                            {tc.mucDanhGias.map((mdg, mdgIndex) => (
                                                <tr 
                                                    key={mdg.id} 
                                                    style={{ cursor: 'pointer', backgroundColor: ketQuaHienTai.mucDanhGiaId?.toLowerCase() === mdg.id?.toLowerCase() ? '#e8f4fd' : 'transparent' }}
                                                    onClick={() => handleSelectMucDanhGia(tc.id, mdg)}
                                                >
                                                    {mdgIndex === 0 && (
                                                        <>
                                                            <td rowSpan={rowSpan} className="text-center align-middle">{indexTc + 1}</td>
                                                            <td rowSpan={rowSpan} className="align-middle">
                                                                {tc.tenTieuChi} <br/> 
                                                                <span className="text-muted small">({tc.diemToiDa}đ)</span>
                                                            </td>
                                                        </>
                                                    )}
                                                    <td className="align-middle">
                                                        <Form.Check 
                                                            type="radio" 
                                                            label={mdg.tenMucDanhGia} 
                                                            name={`radio_${tc.id}`}
                                                            id={`radio_${mdg.id}`}
                                                            checked={ketQuaHienTai.mucDanhGiaId?.toLowerCase() === mdg.id?.toLowerCase()}
                                                            onChange={() => handleSelectMucDanhGia(tc.id, mdg)}
                                                            disabled={readOnly}
                                                            className="m-0"
                                                        />
                                                    </td>
                                                    <td className="text-center align-middle">{mdg.diemSo}</td>
                                                    {mdgIndex === 0 && (
                                                        <td rowSpan={rowSpan} className="text-center align-middle">
                                                            <Form.Control 
                                                                type="number" 
                                                                step="0.5"
                                                                min="0"
                                                                max={tc.diemToiDa}
                                                                value={ketQuaHienTai.diemSo !== undefined && ketQuaHienTai.diemSo !== null ? ketQuaHienTai.diemSo : ''} 
                                                                onChange={(e) => handleDiemSoChange(tc.id, e.target.value)} 
                                                                disabled={readOnly}
                                                                className="text-center font-weight-bold text-danger"
                                                                style={{ width: '80px', margin: '0 auto' }}
                                                            />
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                            {tc.mucDanhGias.length === 0 && (
                                                <tr>
                                                    <td className="text-center align-middle">{indexTc + 1}</td>
                                                    <td className="align-middle">
                                                        {tc.tenTieuChi} <br/> 
                                                        <span className="text-muted small">({tc.diemToiDa}đ)</span>
                                                    </td>
                                                    <td className="text-center text-muted align-middle">-</td>
                                                    <td className="text-center text-muted align-middle">-</td>
                                                    <td className="text-center align-middle">
                                                        <Form.Control 
                                                            type="number" 
                                                            step="0.5"
                                                            min="0"
                                                            max={tc.diemToiDa}
                                                            value={ketQuaHienTai.diemSo !== undefined && ketQuaHienTai.diemSo !== null ? ketQuaHienTai.diemSo : ''} 
                                                            onChange={(e) => handleDiemSoChange(tc.id, e.target.value)} 
                                                            disabled={readOnly}
                                                            className="text-center font-weight-bold text-danger"
                                                            style={{ width: '80px', margin: '0 auto' }}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                        <tr>
                            <td colSpan={4} className="text-right font-weight-bold pr-3 align-middle" style={{ fontSize: '1.1rem' }}>Tổng điểm:</td>
                            <td className="text-center font-weight-bold text-danger align-middle" style={{ fontSize: '1.2rem' }}>{totalScore}</td>
                        </tr>
                    </tbody>
                </Table>
            </div>
            
            <div className="mt-4">
                <Form.Group>
                    <Form.Label className="font-weight-bold">Nhận xét của Hội đồng</Form.Label>
                    <Form.Control 
                        as="textarea" 
                        rows={3} 
                        value={nhanXet} 
                        onChange={(e) => setNhanXet(e.target.value)} 
                        disabled={readOnly}
                        placeholder="Nhập nhận xét đánh giá đề cương..."
                    />
                </Form.Group>
            </div>
            
            {!readOnly && (
                <div className="text-right mt-3">
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <><Spinner size="sm" animation="border" className="mr-2" /> Đang lưu...</> : <><i className="fas fa-save mr-2"></i> Lưu điểm chấm</>}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ChamDiemDeCuongNghienCuu;
